import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, database } from './firebase';
import { ref, get, set, push, onValue, remove } from 'firebase/database';

const FUNCTIONS_BASE = 'https://us-central1-cue-clock-pro.cloudfunctions.net';
async function callFn(name, body) {
  const token = await auth.currentUser.getIdToken(true);
  const resp = await fetch(`${FUNCTIONS_BASE}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Request failed (${resp.status})`);
  }
  return resp.json();
}

const C = {
  bg: '#0d1321', card: '#151c2e', input: '#1c2640', border: '#2a3555',
  green: '#10b981', blue: '#2563eb', purple: '#8b5cf6', amber: '#f59e0b',
  red: '#ef4444', text: '#f0f2f5', muted: '#8b95a8', dim: '#5a6478',
};

const s = {
  page: { minHeight: '100vh', backgroundColor: C.bg, color: C.text, padding: '24px', fontFamily: "'DM Sans', sans-serif" },
  card: { backgroundColor: C.card, borderRadius: '14px', padding: '24px', marginBottom: '20px', border: `1px solid ${C.border}` },
  inp: { backgroundColor: C.input, color: C.text, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', fontSize: '15px', width: '100%', boxSizing: 'border-box' },
  btn: (bg = C.blue, sm) => ({ padding: sm ? '6px 14px' : '10px 20px', backgroundColor: bg, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: sm ? '13px' : '14px', fontWeight: 'bold' }),
  outBtn: (sm) => ({ padding: sm ? '6px 12px' : '8px 16px', backgroundColor: C.input, color: C.muted, border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontSize: sm ? '12px' : '13px' }),
  tab: (a) => ({ padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: a ? '700' : '400', color: a ? C.text : C.muted, backgroundColor: a ? C.input : 'transparent', borderRadius: '8px 8px 0 0', borderBottom: a ? `2px solid ${C.purple}` : '2px solid transparent' }),
  th: { padding: '8px 12px', textAlign: 'left', fontSize: '12px', color: C.muted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${C.border}` },
  td: { padding: '10px 12px', borderBottom: `1px solid ${C.border}33`, fontSize: '14px', verticalAlign: 'middle' },
};

function qualityBadge(q) {
  return ({ high: { color: C.green, label: '● Strong' }, medium: { color: C.amber, label: '● Some' },
    low: { color: C.amber, label: '⚠ Limited' }, none: { color: C.red, label: '⚠ None' } })[q] || { color: C.red, label: '⚠ None' };
}

function toFrac(d) {
  if (!d || d <= 1) return 'N/A';
  const n = d - 1;
  if (n >= 10) return `${Math.round(n)}/1`;
  const fracs = [[10,1],[8,1],[6,1],[5,1],[9,2],[4,1],[7,2],[3,1],[11,4],[5,2],[2,1],[7,4],[6,4],[5,4],[1,1],[4,5],[2,3],[1,2],[1,3],[1,4]];
  let best = fracs[0], bestDiff = Infinity;
  for (const [a,b] of fracs) { const diff = Math.abs(n - a/b); if (diff < bestDiff) { bestDiff = diff; best = [a,b]; } }
  return `${best[0]}/${best[1]}`;
}

function fmt(n, dp = 2) { return Number(n).toFixed(dp); }
function fmtGbp(n) { return `£${Math.abs(n).toFixed(2)}`; }

const FULL_TCS = `PRIVATE SWEEPSTAKE — FULL TERMS & CONDITIONS

1. PRIVATE BETTING BETWEEN FRIENDS
This sweepstake is operated exclusively between members of a private,
invite-only Facebook group. Access is restricted to individuals who have
received the link directly from the organiser. This is not a public betting
service and is not open to the general public.

The organiser acts as bookmaker and holds a financial interest in the outcome
of bets. The organiser may profit or lose depending on results. This is fully
disclosed to all participants, who accept this arrangement by placing a bet.
The organiser does not charge commission or a separate fee for organising the
sweepstake.

This arrangement operates under the social/private betting exemption of the
UK Gambling Act 2005 as betting between persons who personally participate
in a private arrangement. It is not operated as a commercial gambling business.

2. PLACING A BET
A bet is only valid and binding once: (a) the stake money has been physically
received by the organiser, AND (b) the organiser has sent a written
confirmation (receipt) to the bettor. Verbal agreements, messages expressing
intent, or online selections without payment are not valid bets.

3. PRICES
All prices shown are indicative at the time of publication. Odds are subject
to change at any time before a bet is confirmed. The organiser reserves the
right to alter prices in response to withdrawals, new information, or market
conditions. Always confirm the current price before handing over money. The
price on your receipt is your locked price.

4. NON-RUNNERS
If a player does not start the competition for any reason, they are declared
a non-runner. All bets placed on a non-runner will be refunded in full. Where
a non-runner is declared before the event, the organiser will notify all
bettors and recalculate the remaining market.

5. RULE 4 DEDUCTIONS
If a player is withdrawn after bets have been placed, a Rule 4 deduction may
be applied to existing winning bets in accordance with standard bookmaking
practice. The percentage deduction will be proportionate to the withdrawn
player's price at the time of withdrawal, following Tattersalls Rule 4(c)
guidelines. Deductions apply only to bets placed before the withdrawal was
announced. New bets placed after recalculation are not affected.

6. SETTLEMENT
Winning bets will be paid out the day after the competition concludes.
Payment will be made by the same method the stake was received, or by
agreement between the parties.

7. DISPUTES
Any disputes must be raised with the organiser within 48 hours of settlement.
The organiser's decision is final. In the event of a genuine error (e.g.
incorrect price issued on a receipt), the organiser reserves the right to
correct the error and settle at the correct price.

8. GROUP BETS
Group or syndicate bets are not accepted. Each bet must be placed by and in
the name of a single individual. The person named on the receipt is the sole
beneficiary.

9. UNDER 18s
This sweepstake is open to adults aged 18 and over only. By participating you
confirm you are 18 or over. The organiser reserves the right to request proof
of age.

10. RESPONSIBLE GAMBLING
This is intended as a fun, social activity between friends. If gambling is
causing you concern, visit BeGambleAware.org or call the National Gambling
Helpline on 0808 8020 133 (free, 24/7).

11. PRIVACY
No personal data collected in connection with this sweepstake will be shared
with third parties or used for any purpose other than administering the
sweepstake. The Facebook group is private and invite-only. The organiser will
not publish or share the access URL beyond the private group.`;

export default function Book() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [checkingAck, setCheckingAck] = useState(true);
  const [activeTab, setActiveTab] = useState('generate');

  // Generate tab
  const [tournamentId, setTournamentId] = useState('');
  const [lockedTid, setLockedTid] = useState(null); // confirmed tournament for all tabs
  const [compression, setCompression] = useState(0.85);
  const [stake, setStake] = useState(10);
  const [maxLoss, setMaxLoss] = useState(100);
  const [sensitivity, setSensitivity] = useState(0.7);
  const [generating, setGenerating] = useState(false);
  const [oddsData, setOddsData] = useState(null);
  const [overridesText, setOverridesText] = useState('');

  // Published odds
  const [editedOdds, setEditedOdds] = useState({}); // playerId → decimal string
  const [publishedData, setPublishedData] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(null); // null | 'post' | 'terms'
  const [allBooks, setAllBooks] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [hasDeletedPlayers, setHasDeletedPlayers] = useState(false);
  const [recalcMessage, setRecalcMessage] = useState('');
  const [rule4PlayerId, setRule4PlayerId] = useState(null);
  const [rule4Pct, setRule4Pct] = useState(5);
  const [rule4Events, setRule4Events] = useState([]);
  const [rule4Banner, setRule4Banner] = useState('');
  const [termsUrl, setTermsUrl] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Liability tracker
  const [bets, setBets] = useState([]);
  const [betPlayerId, setBetPlayerId] = useState('');
  const [betAmount, setBetAmount] = useState('');
  const [addingBet, setAddingBet] = useState(false);
  const [minProfitFloor, setMinProfitFloor] = useState(0.05);

  // Arb check
  const [dutchStake, setDutchStake] = useState(100);

  const [error, setError] = useState('');

  // ── Auth guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(u => {
      if (u) setUser(u);
      else navigate('/organiser-login');
    });
    return unsub;
  }, [navigate]);

  // ── Acknowledgement check ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    get(ref(database, `organisers/${user.uid}/bookSettings/acknowledged`))
      .then(snap => { setAcknowledged(snap.exists() && snap.val() === true); setCheckingAck(false); });
  }, [user]);

  // ── Load published odds when tournament is locked ──────────────────────────
  useEffect(() => {
    if (!user || !lockedTid) return;
    get(ref(database, `organisers/${user.uid}/oddsEngine/${lockedTid}/published`))
      .then(snap => { if (snap.exists()) setPublishedData(snap.val()); });
  }, [user, lockedTid]);

  // ── Real-time bets listener ────────────────────────────────────────────────
  useEffect(() => {
    if (!user || !lockedTid) return;
    const betsRef = ref(database, `organisers/${user.uid}/oddsEngine/${lockedTid}/bets`);
    const unsub = onValue(betsRef, snap => {
      if (snap.exists()) {
        setBets(Object.entries(snap.val())
          .map(([betId, b]) => ({ betId, ...b }))
          .sort((a, b) => a.timestamp - b.timestamp));
      } else setBets([]);
    });
    return unsub;
  }, [user, lockedTid]);

  // ── Real-time Rule 4 events listener ──────────────────────────────────────
  useEffect(() => {
    if (!user || !lockedTid) return;
    const r4Ref = ref(database, `organisers/${user.uid}/oddsEngine/${lockedTid}/rule4Events`);
    const unsub = onValue(r4Ref, snap => {
      if (snap.exists()) {
        setRule4Events(Object.values(snap.val())
          .sort((a, b) => new Date(a.appliedAt) - new Date(b.appliedAt)));
      } else setRule4Events([]);
    });
    return unsub;
  }, [user, lockedTid]);

  // ── Load termsUrl from organiser settings ─────────────────────────────────
  useEffect(() => {
    if (!user) return;
    get(ref(database, `organisers/${user.uid}/bookSettings/termsUrl`))
      .then(snap => { if (snap.exists()) setTermsUrl(snap.val() || ''); });
  }, [user]);

  // ── Initialise edited odds when oddsData changes ──────────────────────────
  useEffect(() => {
    if (!oddsData) return;
    const init = {};
    oddsData.players.forEach(p => { init[p.playerId] = String(p.offeredDecimal); });
    setEditedOdds(init);
    setLockedTid(String(oddsData.tournamentId));
  }, [oddsData]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleAcknowledge = async () => {
    await set(ref(database, `organisers/${user.uid}/bookSettings`), {
      acknowledged: true, acknowledgedAt: new Date().toISOString(),
    });
    setAcknowledged(true);
  };

  const refreshBooks = useCallback(() => {
    if (!user) return;
    setLoadingBooks(true);
    get(ref(database, `organisers/${user.uid}/oddsEngine`))
      .then(snap => {
        const books = [];
        if (snap.exists()) {
          snap.forEach(child => {
            const pub = child.child('published').val();
            if (pub) {
              books.push({
                tid: child.key,
                tournamentName: pub.tournamentName || child.key,
                publishedAt: pub.publishedAt,
                playerCount: Object.keys(pub.players || {}).length,
              });
            }
          });
          books.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
        }
        setAllBooks(books);
      })
      .finally(() => setLoadingBooks(false));
  }, [user]);

  useEffect(() => { refreshBooks(); }, [refreshBooks]);

  const handleLoadBook = useCallback(async (tid) => {
    if (!user || !tid) return;
    const snap = await get(ref(database, `organisers/${user.uid}/oddsEngine/${tid}/published`));
    if (snap.exists()) {
      const data = snap.val();
      const init = {};
      Object.entries(data.players || {}).forEach(([pid, p]) => { init[pid] = String(p.decimal); });
      setEditedOdds(init);
      setPublishedData(data);
      setLockedTid(tid);
    }
  }, [user]);

  const handleGenerate = async () => {
    if (!tournamentId.trim()) { setError('Please enter a tournament ID.'); return; }
    setGenerating(true); setError(''); setOddsData(null);
    try {
      // Parse manual overrides: "oldId:newId" one per line
      const overrides = {};
      overridesText.trim().split('\n').forEach(line => {
        const [orig, rep] = line.trim().split(':');
        if (orig && rep) overrides[orig.trim()] = rep.trim();
      });
      const result = await callFn('generateTournamentOdds', {
        tournamentId: tournamentId.trim(), compression, stake, maxLoss, overrides, sensitivity,
      });
      // Log per-player sync summary so organiser can verify history was found
      if (result.syncLog) {
        console.log('[Odds] Participant history sync:');
        result.syncLog.forEach(p => {
          const status = p.error ? `❌ error: ${p.error}`
            : `pages=${p.pagesScraped} graded=${p.gradedFound} new=${p.newStored}${p.stored.length ? ` → ${p.stored.join(', ')}` : ''}`;
          console.log(`  ${p.name} (${p.pid}): ${status}`);
        });
      }
      if (result.players) {
        console.log('[Odds] Player breakdown (sorted by probability):');
        result.players.forEach(p => {
          const delta = p.fieldDelta !== undefined ? ` fieldDelta=${p.fieldDelta >= 0 ? '+' : ''}${p.fieldDelta}` : '';
          console.log(`  ${p.name} — prob=${p.probability}% fair=${p.fairDecimal} offered=${p.offeredDecimal} (${p.dataQuality} data, ${p.events} events${delta})`);
          if (p.scoreDetail && p.scoreDetail.length > 0) {
            p.scoreDetail.forEach(ev => {
              console.log(`    [${ev.gradeLabel} gradeVal=${ev.gradeValue} result=${ev.resultScore} recency=${ev.recency} fieldSize=${ev.fieldSizeFactor}] ${ev.tournament} | ${ev.bestFinish} | ${ev.record} | ${ev.monthsAgo}mo ago | abilityPts=${ev.abilityPoints}`);
            });
          } else {
            console.log('    (no history — blended to target grade prior)');
          }
        });
      }
      setOddsData(result);
      setPublishedData(null);
    } catch (err) { setError(err.message); }
    finally { setGenerating(false); }
  };

  const handlePublish = useCallback(async () => {
    if (!oddsData || !user) return;
    setPublishing(true);
    try {
      const players = {};
      oddsData.players.forEach(p => {
        const dec = parseFloat(editedOdds[p.playerId]) || p.offeredDecimal;
        players[p.playerId] = { name: p.name, decimal: dec, fractional: toFrac(dec), tier: p.tier, fairDecimal: p.fairDecimal };
      });
      const data = {
        players, tournamentName: oddsData.tournamentName,
        settings: { compression, stake, maxLoss, minProfitFloor },
        publishedAt: new Date().toISOString(),
      };
      await set(ref(database, `organisers/${user.uid}/oddsEngine/${lockedTid}/published`), data);
      setPublishedData(data);
      setHasDeletedPlayers(false);
      refreshBooks();
    } catch (err) { setError(err.message); }
    finally { setPublishing(false); }
  }, [oddsData, user, lockedTid, editedOdds, compression, stake, maxLoss, minProfitFloor, refreshBooks]);

  const handleCopyFacebookPost = useCallback(() => {
    if (!publishedData) return;
    const sorted = Object.entries(publishedData.players)
      .map(([pid, p]) => ({ ...p, dec: parseFloat(editedOdds[pid]) || p.decimal }))
      .sort((a, b) => a.dec - b.dec);
    const lines = sorted.map(p => `${p.name || '?'} — ${toFrac(p.dec)}`);
    const tcsLine = termsUrl.trim() ? `\n📋 Full T&Cs: ${termsUrl.trim()}` : '';
    const text = [
      `🎱 ${publishedData.tournamentName} — Sweepstake`, '',
      ...lines, tcsLine, '',
      '• Bets binding only once money received & receipt confirmed',
      '• Prices subject to change — check latest before betting',
      '• Non-runners = full refund',
      '• Rule 4 deductions may apply on withdrawals',
      '• Winnings paid the day after the comp',
      '• Private bets only — group members only',
      '• No group betting',
    ].filter(l => l !== undefined).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied('post');
      setTimeout(() => setCopied(null), 2000);
    });
  }, [publishedData, editedOdds, termsUrl]);

  const handleCopyTerms = useCallback(() => {
    navigator.clipboard.writeText(FULL_TCS).then(() => {
      setCopied('terms');
      setTimeout(() => setCopied(null), 2000);
    });
  }, []);

  const handleSaveTermsUrl = useCallback(async (url) => {
    if (!user) return;
    await set(ref(database, `organisers/${user.uid}/bookSettings/termsUrl`), url);
  }, [user]);

  const handleRecalculate = useCallback(async () => {
    if (!publishedData || !user || !lockedTid) return;
    const remaining = Object.entries(publishedData.players);
    const n = remaining.length;
    if (n < 2) return;
    const { compression: c = 0.85, stake: st = 10, maxLoss: ml = 100 } = publishedData.settings || {};
    // Use fairDecimal if stored, fall back to offered decimal
    const fairProbs = remaining.map(([, p]) => 1 / (p.fairDecimal || p.decimal));
    const total = fairProbs.reduce((s, v) => s + v, 0);
    const normProbs = fairProbs.map(v => v / total);
    // Assign tiers by normalised probability (proxy for ability rank)
    const topCount = Math.round(n * 0.50);
    const midCount = Math.round(n * 0.30);
    const midCap = n * 0.9;
    const t2Cap = n + ml / st;
    const indexed = remaining.map(([pid], i) => ({ pid, prob: normProbs[i] }))
      .sort((a, b) => b.prob - a.prob);
    const tierMap = {};
    indexed.forEach(({ pid }, i) => {
      if (i < topCount)                  tierMap[pid] = 'top';
      else if (i < topCount + midCount)  tierMap[pid] = 'mid';
      else                               tierMap[pid] = 'bottom';
    });
    const updatedPlayers = {};
    remaining.forEach(([pid, p], i) => {
      const fairDec = 1 / normProbs[i];
      const pt = tierMap[pid];
      const tier = pt === 'bottom' ? 2 : 1;
      let raw;
      if (pt === 'top')       raw = fairDec * c;
      else if (pt === 'mid')  raw = Math.min(fairDec * c, midCap);
      else                    raw = Math.min(fairDec, t2Cap);
      const offered = Math.round(raw * 2) / 2;
      const fNum = offered - 1;
      const fractional = fNum >= 1 ? `${Math.round(fNum)}/1` : fNum >= 0.5 ? `1/2` : `1/${Math.round(1 / fNum)}`;
      updatedPlayers[pid] = { ...p, decimal: offered, fractional, tier, fairDecimal: Math.round(fairDec * 10) / 10 };
    });
    const updatedData = { ...publishedData, players: updatedPlayers };
    await set(ref(database, `organisers/${user.uid}/oddsEngine/${lockedTid}/published`), updatedData);
    setPublishedData(updatedData);
    const init = {};
    Object.entries(updatedPlayers).forEach(([pid, p]) => { init[pid] = String(p.decimal); });
    setEditedOdds(init);
    setHasDeletedPlayers(false);
    setRecalcMessage(`Odds recalculated for ${n} players`);
    setTimeout(() => setRecalcMessage(''), 4000);
    refreshBooks();
  }, [publishedData, user, lockedTid, refreshBooks]);

  const handleApplyRule4 = useCallback(async (pid) => {
    if (!user || !lockedTid || !publishedData) return;
    const player = publishedData.players[pid];
    if (!player) return;
    const appliedAt = new Date().toISOString();
    const priorBetsCount = bets.filter(b => b.playerId !== pid).length;
    await push(ref(database, `organisers/${user.uid}/oddsEngine/${lockedTid}/rule4Events`), {
      removedPlayer: player.name, removedPlayerId: pid,
      deductionPct: rule4Pct, appliedAt, appliedToCount: priorBetsCount,
    });
    setRule4PlayerId(null);
    setRule4Banner(`Rule 4: ${rule4Pct}% deduction applied to ${priorBetsCount} existing bet(s) placed before ${new Date(appliedAt).toLocaleTimeString('en-GB')}. New bets use recalculated prices.`);
    setTimeout(() => setRule4Banner(''), 10000);
  }, [user, lockedTid, publishedData, bets, rule4Pct]);

  const handleAddBet = async () => {
    if (!betPlayerId || !betAmount || !publishedData) return;
    const player = publishedData.players[betPlayerId];
    if (!player) return;
    setAddingBet(true);
    try {
      await push(ref(database, `organisers/${user.uid}/oddsEngine/${lockedTid}/bets`), {
        playerId: betPlayerId, playerName: player.name,
        amount: parseFloat(betAmount), decimal: player.decimal,
        timestamp: Date.now(),
      });
      setBetAmount('');
    } catch (err) { setError(err.message); }
    finally { setAddingBet(false); }
  };

  const handleDeleteBet = async (betId) => {
    await remove(ref(database, `organisers/${user.uid}/oddsEngine/${lockedTid}/bets/${betId}`));
  };

  const handleDeletePlayer = useCallback(async (pid) => {
    if (!user || !lockedTid || !publishedData) return;
    const updatedPlayers = { ...publishedData.players };
    delete updatedPlayers[pid];
    const updatedData = { ...publishedData, players: updatedPlayers };
    await set(ref(database, `organisers/${user.uid}/oddsEngine/${lockedTid}/published`), updatedData);
    setPublishedData(updatedData);
    setEditedOdds(prev => { const next = { ...prev }; delete next[pid]; return next; });
    setHasDeletedPlayers(true);
    refreshBooks();
  }, [user, lockedTid, publishedData, refreshBooks]);

  // ── Liability calculations ─────────────────────────────────────────────────
  const liabilityCalc = useCallback(() => {
    if (!publishedData) return null;
    const totalPot = bets.reduce((s, b) => s + b.amount, 0);
    const stakeByPlayer = {};
    bets.forEach(b => { stakeByPlayer[b.playerId] = (stakeByPlayer[b.playerId] || 0) + b.amount; });

    const rows = Object.entries(publishedData.players).map(([pid, p]) => {
      const totalStaked = stakeByPlayer[pid] || 0;
      const exposure = totalStaked * p.decimal;
      const houseWin = totalPot - exposure;
      const profitPct = totalPot > 0 ? houseWin / totalPot : null;
      const maxSafe = totalPot > 0
        ? Math.max(0, (totalPot * (1 - minProfitFloor)) / p.decimal - totalStaked)
        : null;
      const alert = totalPot > 0 && profitPct !== null && profitPct < minProfitFloor;
      return { pid, name: p.name, decimal: p.decimal, totalStaked, exposure, houseWin, profitPct, maxSafe, alert, unbacked: totalStaked === 0 };
    });
    rows.sort((a, b) => b.totalStaked - a.totalStaked);
    return { totalPot, rows };
  }, [bets, publishedData, minProfitFloor]);

  // ── Arb calculations ───────────────────────────────────────────────────────
  const arbCalc = useCallback(() => {
    if (!publishedData) return null;
    const players = Object.entries(publishedData.players).map(([pid, p]) => ({ pid, ...p }));
    const bookPct = players.reduce((s, p) => s + 1 / p.decimal, 0) * 100;
    const dutchRows = players.map(p => {
      const stake = dutchStake * (1 / p.decimal) / (bookPct / 100);
      const payout = stake * p.decimal;
      return { ...p, dutchStake: stake, dutchPayout: payout };
    });
    const dutchReturn = dutchStake * (100 / bookPct);
    const dutchProfit = dutchReturn - dutchStake;
    return { bookPct: Math.round(bookPct * 10) / 10, dutchRows, dutchReturn, dutchProfit, safe: bookPct >= 100 };
  }, [publishedData, dutchStake]);

  // ── Loading / not acknowledged ─────────────────────────────────────────────
  if (!user || checkingAck) return <div style={s.page}><p style={{ color: C.muted, marginTop: '100px', textAlign: 'center' }}>Loading…</p></div>;

  if (!acknowledged) {
    return (
      <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ ...s.card, maxWidth: '560px', border: `1px solid ${C.purple}66` }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', fontFamily: "'Outfit', sans-serif" }}>🎱 Odds Engine — Before You Continue</h2>
          <p style={{ color: C.muted, fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
            This tool generates odds and records stakes for <strong style={{ color: C.text }}>private pool tournaments</strong>. It operates within the social/private betting exemption of the UK Gambling Act 2005.
          </p>
          <div style={{ backgroundColor: C.input, borderRadius: '8px', padding: '14px 16px', marginBottom: '20px', fontSize: '13px', lineHeight: '1.7', color: C.muted }}>
            By continuing, you confirm:
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>Betting is <strong style={{ color: C.text }}>private</strong> — known tournament participants only</li>
              <li>No platform fee or commission will be charged</li>
              <li>No participants are under 18</li>
              <li>This is incidental to your pool tournament — not a standalone commercial operation</li>
              <li>Payments are handled directly between participants — this app does <strong style={{ color: C.text }}>not</strong> process gambling transactions</li>
            </ul>
          </div>
          <p style={{ fontSize: '12px', color: C.dim, marginBottom: '20px' }}>Opening betting to a wider public or charging a platform fee may require a Gambling Commission licence.</p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleAcknowledge} style={s.btn(C.purple)}>✓ I understand — Continue</button>
            <button onClick={() => navigate('/organiser-dashboard')} style={s.outBtn()}>← Back</button>
          </div>
        </div>
      </div>
    );
  }

  const liability = liabilityCalc();
  const arb = arbCalc();

  return (
    <div style={s.page}>
      <div style={{ maxWidth: '1300px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '26px', fontWeight: '700', fontFamily: "'Outfit', sans-serif" }}>🎰 Odds Engine</div>
            <div style={{ fontSize: '13px', color: C.muted }}>
              Private tournament odds — organiser tool only
              {lockedTid && <span style={{ marginLeft: '10px', color: C.purple }}>Tournament: {lockedTid}</span>}
            </div>
          </div>
          <button onClick={() => navigate('/organiser-dashboard')} style={s.outBtn()}>← Dashboard</button>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${C.border}`, marginBottom: '20px' }}>
          {[
            { id: 'generate', label: '📊 Generate' },
            { id: 'published', label: '📋 Published Odds' },
            { id: 'liability', label: '💷 Liability' },
            { id: 'arb', label: '🛡 Arb Check' },
          ].map(t => <button key={t.id} onClick={() => setActiveTab(t.id)} style={s.tab(activeTab === t.id)}>{t.label}</button>)}
        </div>

        {error && (
          <div style={{ padding: '10px 14px', backgroundColor: `${C.red}11`, border: `1px solid ${C.red}44`, borderRadius: '8px', fontSize: '13px', color: C.red, marginBottom: '16px' }}>
            ⚠ {error} <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', float: 'right' }}>✕</button>
          </div>
        )}

        {/* ════════════════════ GENERATE TAB ════════════════════ */}
        {activeTab === 'generate' && (
          <>
            <div style={s.card}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', fontFamily: "'Outfit', sans-serif" }}>Tournament & Settings</h3>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: C.muted, marginBottom: '6px' }}>CueScore Tournament ID</label>
                  <input style={s.inp} value={tournamentId} onChange={e => setTournamentId(e.target.value)} placeholder="e.g. 64040800" />
                  <div style={{ fontSize: '11px', color: C.dim, marginTop: '4px' }}>cuescore.com/tournament/…/<strong>{tournamentId || 'ID'}</strong></div>
                </div>
                <div style={{ minWidth: '160px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: C.muted, marginBottom: '6px' }}>Compression: {Math.round(compression * 100)}%</label>
                  <input type="range" min="0.70" max="0.95" step="0.01" value={compression} onChange={e => setCompression(parseFloat(e.target.value))} style={{ width: '100%', accentColor: C.purple }} />
                  <div style={{ fontSize: '11px', color: C.dim }}>Book≈{Math.round(100 / compression)}% · fair 10/1→{(11 * compression - 1).toFixed(1)}/1</div>
                </div>
                <div style={{ minWidth: '160px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: C.muted, marginBottom: '6px' }}>Market Spread: {sensitivity.toFixed(2)}</label>
                  <input type="range" min="0.3" max="1.2" step="0.05" value={sensitivity} onChange={e => setSensitivity(parseFloat(e.target.value))} style={{ width: '100%', accentColor: C.green }} />
                  <div style={{ fontSize: '11px', color: C.dim }}>Tight spread (0.3) → wide spread (1.2) · default 0.7</div>
                </div>
                <div style={{ minWidth: '110px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: C.muted, marginBottom: '6px' }}>Equal stake (£)</label>
                  <input type="number" min="1" step="1" value={stake} onChange={e => setStake(parseInt(e.target.value) || 10)} style={{ ...s.inp, width: '80px', padding: '6px 10px' }} />
                  <div style={{ fontSize: '11px', color: C.dim, marginTop: '3px' }}>Used for P&L cap calc</div>
                </div>
                <div style={{ minWidth: '110px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: C.muted, marginBottom: '6px' }}>Max loss (£)</label>
                  <input type="number" min="1" step="10" value={maxLoss} onChange={e => setMaxLoss(parseInt(e.target.value) || 100)} style={{ ...s.inp, width: '80px', padding: '6px 10px' }} />
                  <div style={{ fontSize: '11px', color: C.dim, marginTop: '3px' }}>Tier 2 worst-case loss</div>
                </div>
                <div style={{ minWidth: '220px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: C.muted, marginBottom: '6px' }}>
                    Player ID overrides <span style={{ color: C.dim }}>(optional — oldId:newId per line)</span>
                  </label>
                  <textarea
                    rows={2}
                    style={{ ...s.inp, fontSize: '12px', resize: 'vertical', fontFamily: 'monospace' }}
                    value={overridesText}
                    onChange={e => setOverridesText(e.target.value)}
                    placeholder={'12345:67890\n11111:22222'}
                  />
                  <div style={{ fontSize: '11px', color: C.dim, marginTop: '3px' }}>Use when a "Player A / Player B" account needs replacing with a real player ID</div>
                </div>
                <button onClick={handleGenerate} disabled={generating} style={{ ...s.btn(generating ? C.dim : C.purple), alignSelf: 'flex-end' }}>
                  {generating ? '⏳ Generating…' : '🎰 Generate Odds'}
                </button>
              </div>
            </div>

            {oddsData && (
              <div style={s.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: "'Outfit', sans-serif", marginBottom: '2px' }}>{oddsData.tournamentName}</h3>
                    <div style={{ fontSize: '13px', color: C.muted }}>Grade: <strong style={{ color: C.text }}>{oddsData.targetGrade}</strong> · {oddsData.players.length} participants · {new Date(oddsData.generatedAt).toLocaleTimeString('en-GB')}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '22px', fontWeight: 'bold', color: oddsData.bookPct >= 105 ? C.green : oddsData.bookPct >= 100 ? C.amber : C.red }}>{oddsData.bookPct}%</div>
                    <div style={{ fontSize: '11px', color: C.muted }}>Generated book%</div>
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>{['#','Player','Grade','Win %','Fair','Offered','Frac','Data','Tier'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {oddsData.players.map((p, idx) => {
                        const qb = qualityBadge(p.dataQuality);
                        return (
                          <tr key={p.playerId} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : '#ffffff06' }}>
                            <td style={{ ...s.td, color: C.muted }}>{idx + 1}</td>
                            <td style={s.td}>
                              <a href={`https://cuescore.com/player/${(p.name||'').replace(/\s+/g,'+')}/${p.playerId}/tournaments`}
                                target="_blank" rel="noopener noreferrer"
                                style={{ color: C.text, textDecoration: 'none', fontWeight: '600' }}
                                onMouseEnter={e => { e.target.style.color = C.blue; }} onMouseLeave={e => { e.target.style.color = C.text; }}>
                                {p.name}
                              </a>
                              {p.noHistory && <span style={{ marginLeft: '6px', fontSize: '11px', color: C.red }}>⚠ no history</span>}
                            </td>
                            <td style={{ ...s.td, color: C.muted }}>{p.storedGrade || '—'}</td>
                            <td style={s.td}>{p.probability}%</td>
                            <td style={{ ...s.td, color: C.muted }}>{p.fairDecimal}</td>
                            <td style={{ ...s.td, fontWeight: 'bold', fontSize: '16px' }}>{p.offeredDecimal}</td>
                            <td style={{ ...s.td, color: C.muted }}>{toFrac(p.offeredDecimal)}</td>
                            <td style={{ ...s.td, fontSize: '12px', color: qb.color }}>{qb.label}</td>
                            <td style={s.td}><span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', backgroundColor: p.tier === 1 ? `${C.blue}22` : `${C.amber}22`, color: p.tier === 1 ? C.blue : C.amber }}>T{p.tier}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '16px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => { handlePublish(); setActiveTab('published'); }} disabled={publishing} style={s.btn(publishing ? C.dim : C.green)}>
                    {publishing ? '⏳ Publishing…' : '📋 Publish Odds'}
                  </button>
                  <span style={{ fontSize: '12px', color: C.dim }}>Locks these prices and opens the Liability Tracker</span>
                </div>
                {oddsData.players.some(p => p.noHistory) && (
                  <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: `${C.amber}11`, border: `1px solid ${C.amber}33`, borderRadius: '8px', fontSize: '13px', color: C.amber }}>
                    ⚠ {oddsData.players.filter(p => p.noHistory).length} player(s) have no stored history — odds based on equal probability. Consider pricing conservatively or not offering on these players.
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ════════════════════ PUBLISHED ODDS TAB ════════════════════ */}
        {activeTab === 'published' && (
          <>
            <div style={s.card}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '13px', color: C.muted, whiteSpace: 'nowrap' }}>Load a book:</label>
                <select
                  value={lockedTid || ''}
                  onChange={e => handleLoadBook(e.target.value)}
                  style={{ ...s.inp, flex: 1, minWidth: '220px' }}
                >
                  <option value="">— Select tournament —</option>
                  {allBooks.map(b => (
                    <option key={b.tid} value={b.tid}>
                      {b.tournamentName} · {b.playerCount} players · {new Date(b.publishedAt).toLocaleDateString('en-GB')}
                    </option>
                  ))}
                </select>
                {loadingBooks && <span style={{ fontSize: '12px', color: C.muted }}>Loading…</span>}
                {!loadingBooks && allBooks.length === 0 && (
                  <span style={{ fontSize: '12px', color: C.dim }}>No published books yet — generate and publish one first.</span>
                )}
              </div>
            </div>

            {!publishedData ? (
              <div style={{ ...s.card, textAlign: 'center', padding: '60px' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
                <p style={{ color: C.muted }}>Generate odds first, then click Publish Odds to lock in prices.</p>
              </div>
            ) : (
              <div style={s.card}>
                {/* Header row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: "'Outfit', sans-serif", marginBottom: '4px' }}>
                      {publishedData.tournamentName} — Published Prices
                    </h3>
                    <div style={{ fontSize: '12px', color: C.dim }}>Published {new Date(publishedData.publishedAt).toLocaleString('en-GB')} · Edit below and re-publish to update</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {hasDeletedPlayers && (
                      <button onClick={handleRecalculate} style={s.btn(C.amber, true)}>⟳ Recalculate Odds</button>
                    )}
                    <button onClick={handleCopyFacebookPost} style={s.btn(copied === 'post' ? C.green : C.purple, true)}>
                      {copied === 'post' ? '✓ Copied!' : '📱 Facebook Post'}
                    </button>
                    <button onClick={() => setShowTermsModal(true)} style={s.outBtn(true)}>📄 T&Cs</button>
                    <button onClick={handlePublish} disabled={publishing} style={s.btn(publishing ? C.dim : C.green, true)}>
                      {publishing ? '⏳ Saving…' : '💾 Update Prices'}
                    </button>
                  </div>
                </div>

                {/* Banners */}
                {recalcMessage && (
                  <div style={{ padding: '8px 14px', backgroundColor: `${C.amber}22`, border: `1px solid ${C.amber}44`, borderRadius: '8px', fontSize: '13px', color: C.amber, marginBottom: '12px' }}>
                    ✓ {recalcMessage}
                  </div>
                )}
                {rule4Banner && (
                  <div style={{ padding: '8px 14px', backgroundColor: `${C.red}11`, border: `1px solid ${C.red}33`, borderRadius: '8px', fontSize: '13px', color: C.red, marginBottom: '12px' }}>
                    ⚠ {rule4Banner}
                  </div>
                )}

                {/* Settings row */}
                <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: C.muted }}>Min profit floor:</span>
                  <input type="range" min="0" max="0.20" step="0.01" value={minProfitFloor}
                    onChange={e => setMinProfitFloor(parseFloat(e.target.value))}
                    style={{ width: '120px', accentColor: C.green }} />
                  <span style={{ fontSize: '13px', color: C.text }}>{Math.round(minProfitFloor * 100)}%</span>
                  <span style={{ fontSize: '12px', color: C.dim, marginRight: '16px' }}>House keeps at least this % of the pot regardless of winner</span>
                  <span style={{ fontSize: '13px', color: C.muted }}>T&Cs URL:</span>
                  <input
                    style={{ ...s.inp, width: '260px', fontSize: '13px', padding: '6px 10px' }}
                    value={termsUrl}
                    onChange={e => setTermsUrl(e.target.value)}
                    onBlur={e => handleSaveTermsUrl(e.target.value)}
                    placeholder="https://your-site.com/sweepstake-terms"
                  />
                </div>

                {/* Odds table */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>{['Player','Tier','Decimal (edit)','Fractional','Book contrib','Rule 4',''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {Object.entries(publishedData.players)
                        .sort(([pidA, a], [pidB, b]) => (parseFloat(editedOdds[pidA]) || a.decimal) - (parseFloat(editedOdds[pidB]) || b.decimal))
                        .map(([pid, p], idx) => {
                          const dec = parseFloat(editedOdds[pid]) || p.decimal;
                          return (
                            <React.Fragment key={pid}>
                              <tr style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : '#ffffff06' }}>
                                <td style={{ ...s.td, fontWeight: '600' }}>
                                  <a href={`https://cuescore.com/player/${(p.name||'').replace(/\s+/g,'+')}/${pid}/tournaments`}
                                    target="_blank" rel="noopener noreferrer" style={{ color: C.text, textDecoration: 'none' }}
                                    onMouseEnter={e => { e.target.style.color = C.blue; }} onMouseLeave={e => { e.target.style.color = C.text; }}>
                                    {p.name}
                                  </a>
                                </td>
                                <td style={s.td}><span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', backgroundColor: p.tier === 1 ? `${C.blue}22` : `${C.amber}22`, color: p.tier === 1 ? C.blue : C.amber }}>T{p.tier}</span></td>
                                <td style={s.td}>
                                  <input type="number" min="1.1" step="0.5"
                                    value={editedOdds[pid] ?? p.decimal}
                                    onChange={e => setEditedOdds(prev => ({ ...prev, [pid]: e.target.value }))}
                                    style={{ ...s.inp, width: '90px', padding: '6px 10px', fontSize: '15px', fontWeight: 'bold' }}
                                  />
                                </td>
                                <td style={{ ...s.td, color: C.muted }}>{toFrac(dec)}</td>
                                <td style={{ ...s.td, color: C.muted, fontSize: '13px' }}>{fmt(100 / dec, 1)}%</td>
                                <td style={s.td}>
                                  <button onClick={() => setRule4PlayerId(rule4PlayerId === pid ? null : pid)}
                                    style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: '11px', padding: '2px 8px', borderRadius: '4px' }}>
                                    R4
                                  </button>
                                </td>
                                <td style={s.td}>
                                  <button onClick={() => handleDeletePlayer(pid)} title="Remove player"
                                    style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', fontSize: '16px', padding: '2px 6px', borderRadius: '4px' }}
                                    onMouseEnter={e => { e.currentTarget.style.color = C.red; }}
                                    onMouseLeave={e => { e.currentTarget.style.color = C.dim; }}>✕</button>
                                </td>
                              </tr>
                              {rule4PlayerId === pid && (
                                <tr>
                                  <td colSpan={7} style={{ padding: '10px 12px', backgroundColor: `${C.red}0a`, borderBottom: `1px solid ${C.border}` }}>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', fontSize: '13px' }}>
                                      <span style={{ color: C.muted }}>Rule 4 deduction for <strong style={{ color: C.text }}>{p.name}</strong>:</span>
                                      <select value={rule4Pct} onChange={e => setRule4Pct(parseInt(e.target.value))}
                                        style={{ ...s.inp, width: 'auto', padding: '4px 8px', fontSize: '13px' }}>
                                        {[5,10,15,20,25].map(n => <option key={n} value={n}>{n}% deduction</option>)}
                                      </select>
                                      <button onClick={() => handleApplyRule4(pid)} style={s.btn(C.red, true)}>Apply Rule 4</button>
                                      <button onClick={() => setRule4PlayerId(null)} style={s.outBtn(true)}>Cancel</button>
                                      <span style={{ color: C.dim, fontSize: '12px' }}>Reduces payout of all existing bets by {rule4Pct}%</span>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} style={{ ...s.td, fontWeight: 'bold', color: C.text }}>Book %</td>
                        <td colSpan={2} style={{ ...s.td, fontWeight: 'bold', fontSize: '16px', color: (() => { const bp = Object.values(editedOdds).reduce((s, d) => s + 100 / parseFloat(d||1), 0); return bp >= 105 ? C.green : bp >= 100 ? C.amber : C.red; })() }}>
                          {fmt(Object.entries(publishedData.players).reduce((sum, [pid]) => sum + 100 / (parseFloat(editedOdds[pid]) || publishedData.players[pid]?.decimal || 2), 0), 1)}%
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Rule 4 history */}
                {rule4Events.length > 0 && (
                  <div style={{ marginTop: '14px', padding: '10px 14px', backgroundColor: `${C.red}08`, border: `1px solid ${C.red}22`, borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: C.muted, marginBottom: '6px' }}>Rule 4 History</div>
                    {rule4Events.map((ev, i) => (
                      <div key={i} style={{ fontSize: '12px', color: C.dim, marginBottom: '2px' }}>
                        {new Date(ev.appliedAt).toLocaleTimeString('en-GB')} — {ev.deductionPct}% deduction after {ev.removedPlayer} withdrawal · applied to {ev.appliedToCount} bet(s)
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ════════════════════ LIABILITY TAB ════════════════════ */}
        {activeTab === 'liability' && (
          <>
            {!publishedData ? (
              <div style={{ ...s.card, textAlign: 'center', padding: '60px' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>💷</div>
                <p style={{ color: C.muted }}>Publish odds first to enable the liability tracker.</p>
              </div>
            ) : (
              <>
                {/* Add bet */}
                <div style={s.card}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '14px' }}>Record a Stake</h3>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ minWidth: '180px' }}>
                      <label style={{ fontSize: '13px', color: C.muted, display: 'block', marginBottom: '5px' }}>Player</label>
                      <select value={betPlayerId} onChange={e => setBetPlayerId(e.target.value)} style={{ ...s.inp, width: 'auto' }}>
                        <option value="">Select player…</option>
                        {Object.entries(publishedData.players).map(([pid, p]) => (
                          <option key={pid} value={pid}>{p.name} ({p.decimal})</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ minWidth: '120px' }}>
                      <label style={{ fontSize: '13px', color: C.muted, display: 'block', marginBottom: '5px' }}>Stake (£)</label>
                      <input type="number" min="0.01" step="0.50" value={betAmount}
                        onChange={e => setBetAmount(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleAddBet(); }}
                        style={{ ...s.inp, width: '110px' }} placeholder="0.00" />
                    </div>
                    <button onClick={handleAddBet} disabled={addingBet || !betPlayerId || !betAmount} style={s.btn(C.green)}>
                      + Record Stake
                    </button>
                  </div>
                </div>

                {/* Liability summary */}
                {liability && (
                  <div style={s.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>Liability Overview</h3>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: C.green }}>£{fmt(liability.totalPot)}</div>
                        <div style={{ fontSize: '12px', color: C.muted }}>Total pot · {bets.length} stake{bets.length !== 1 ? 's' : ''}</div>
                      </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>{['Player','Decimal','Total staked','Payout if wins','House P&L','Max safe bet','Status'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {liability.rows.map((row, idx) => (
                            <tr key={row.pid} style={{ backgroundColor: row.unbacked ? `${C.green}08` : row.alert ? `${C.red}08` : idx % 2 === 0 ? 'transparent' : '#ffffff06' }}>
                              <td style={{ ...s.td, fontWeight: '600' }}>{row.name}</td>
                              <td style={s.td}>{row.decimal}</td>
                              <td style={s.td}>{fmtGbp(row.totalStaked)}</td>
                              <td style={{ ...s.td, color: row.exposure > liability.totalPot * (1 - minProfitFloor) ? C.red : C.text }}>{fmtGbp(row.exposure)}</td>
                              <td style={{ ...s.td, fontWeight: 'bold', color: row.houseWin >= 0 ? C.green : C.red }}>
                                {row.houseWin >= 0 ? '+' : ''}{fmtGbp(row.houseWin)}
                                {row.profitPct !== null && <span style={{ fontSize: '11px', color: C.muted, marginLeft: '4px' }}>({fmt(row.profitPct * 100, 1)}%)</span>}
                              </td>
                              <td style={{ ...s.td, color: C.muted }}>{row.maxSafe !== null ? fmtGbp(row.maxSafe) : '—'}</td>
                              <td style={s.td}>
                                {row.unbacked
                                  ? <span style={{ color: C.green, fontSize: '12px', fontWeight: 'bold' }}>✓ Unbacked — best outcome</span>
                                  : row.alert
                                  ? <span style={{ color: C.red, fontSize: '12px', fontWeight: 'bold' }}>⚠ Floor breached — shorten price</span>
                                  : <span style={{ color: C.muted, fontSize: '12px' }}>✓ Within floor</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <p style={{ marginTop: '12px', fontSize: '12px', color: C.dim }}>
                      Green rows = unbacked players — if they win, the house keeps the entire pot. Never offset these.
                      Max safe bet = how much more can be staked on this player before the profit floor is breached.
                    </p>
                  </div>
                )}

                {/* Bet log */}
                {bets.length > 0 && (
                  <div style={s.card}>
                    <h3 style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '12px' }}>Stake Log</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>{['Time','Player','Stake','Decimal','Payout',''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {bets.map(b => {
                          const r4Multiplier = rule4Events.reduce((m, ev) => {
                            return b.timestamp < new Date(ev.appliedAt).getTime() ? m * (1 - ev.deductionPct / 100) : m;
                          }, 1);
                          const origPayout = b.amount * b.decimal;
                          const adjPayout = origPayout * r4Multiplier;
                          const hasR4 = r4Multiplier < 1;
                          return (
                            <tr key={b.betId}>
                              <td style={{ ...s.td, fontSize: '12px', color: C.muted }}>{new Date(b.timestamp).toLocaleTimeString('en-GB')}</td>
                              <td style={{ ...s.td, fontWeight: '600' }}>{b.playerName}</td>
                              <td style={s.td}>£{fmt(b.amount)}</td>
                              <td style={{ ...s.td, color: C.muted }}>{b.decimal}</td>
                              <td style={s.td}>
                                {hasR4
                                  ? <span><span style={{ textDecoration: 'line-through', color: C.dim }}>£{fmt(origPayout)}</span>{' → '}<strong>£{fmt(adjPayout)}</strong><span style={{ fontSize: '11px', color: C.red, marginLeft: '4px' }}>Rule 4 -{Math.round((1 - r4Multiplier) * 100)}%</span></span>
                                  : `£${fmt(origPayout)}`}
                              </td>
                              <td style={s.td}><button onClick={() => handleDeleteBet(b.betId)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: '16px' }}>✕</button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ════════════════════ ARB CHECK TAB ════════════════════ */}
        {activeTab === 'arb' && (
          <>
            {!publishedData ? (
              <div style={{ ...s.card, textAlign: 'center', padding: '60px' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🛡</div>
                <p style={{ color: C.muted }}>Publish odds first to run the arb check.</p>
              </div>
            ) : arb && (
              <>
                {/* Book % gauge */}
                <div style={s.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: "'Outfit', sans-serif" }}>Book Percentage</h3>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '36px', fontWeight: 'bold', color: arb.safe ? C.green : C.red }}>{arb.bookPct}%</div>
                      <div style={{ fontSize: '13px', color: C.muted }}>{arb.safe ? '✓ Arb-safe' : '⚠ VULNERABLE — below 100%'}</div>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div style={{ height: '12px', borderRadius: '6px', backgroundColor: C.input, marginBottom: '8px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(arb.bookPct, 130)}%`, backgroundColor: arb.bookPct >= 110 ? C.green : arb.bookPct >= 100 ? C.amber : C.red, transition: 'width 0.3s', borderRadius: '6px' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: C.dim }}>
                    <span>0%</span><span style={{ color: C.red }}>100% = arb threshold</span><span>130%+</span>
                  </div>
                  {!arb.safe && (
                    <div style={{ marginTop: '12px', padding: '12px 14px', backgroundColor: `${C.red}11`, border: `1px solid ${C.red}44`, borderRadius: '8px', fontSize: '13px', color: C.red }}>
                      ⚠ <strong>Book is below 100%.</strong> A bettor can dutch all players and guarantee profit of {fmtGbp(Math.abs(arb.dutchProfit))} per £{dutchStake} staked. Shorten your longest prices immediately.
                    </div>
                  )}
                </div>

                {/* Dutch simulator */}
                <div style={s.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>Dutch Bet Simulator</h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <label style={{ fontSize: '13px', color: C.muted }}>Simulate £</label>
                      <input type="number" min="1" value={dutchStake} onChange={e => setDutchStake(parseFloat(e.target.value) || 100)}
                        style={{ ...s.inp, width: '80px', padding: '6px 10px' }} />
                    </div>
                  </div>
                  <p style={{ fontSize: '13px', color: C.muted, marginBottom: '14px' }}>
                    If a bettor spreads £{dutchStake} proportionally across all players, they get back{' '}
                    <strong style={{ color: arb.dutchReturn < dutchStake ? C.green : C.red }}>£{fmt(arb.dutchReturn)}</strong>
                    {' '}regardless of winner — a <strong style={{ color: arb.dutchProfit <= 0 ? C.green : C.red }}>{arb.dutchProfit <= 0 ? 'loss' : 'profit'} of {fmtGbp(arb.dutchProfit)}</strong>.
                    {arb.safe ? ' House is protected ✓' : ' ⚠ House is exposed'}
                  </p>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>{['Player','Decimal','Dutch stake','Return'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {arb.dutchRows.map((p, idx) => (
                        <tr key={p.pid} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : '#ffffff06' }}>
                          <td style={{ ...s.td, fontWeight: '600' }}>{p.name}</td>
                          <td style={s.td}>{p.decimal}</td>
                          <td style={{ ...s.td, color: C.muted }}>£{fmt(p.dutchStake)}</td>
                          <td style={{ ...s.td, color: C.muted }}>£{fmt(p.dutchPayout)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* ════════════════════ T&Cs MODAL ════════════════════ */}
      {showTermsModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: '#000000cc', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
          onClick={() => setShowTermsModal(false)}>
          <div style={{ backgroundColor: C.card, borderRadius: '14px', border: `1px solid ${C.border}`, maxWidth: '680px', width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>Private Sweepstake — Full T&Cs</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleCopyTerms} style={s.btn(copied === 'terms' ? C.green : C.purple, true)}>
                  {copied === 'terms' ? '✓ Copied!' : '📋 Copy T&Cs'}
                </button>
                <button onClick={() => setShowTermsModal(false)} style={s.outBtn(true)}>✕ Close</button>
              </div>
            </div>
            <pre style={{ padding: '20px 24px', overflowY: 'auto', fontSize: '12px', lineHeight: '1.7', color: C.muted, whiteSpace: 'pre-wrap', margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
              {FULL_TCS}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
